import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import PageNav from "../../components/common/pageNav.comp";
import Layout from "../../components/layouts/layout.comp";
import UploadMediaForm from "../../components/media/uploadMedia.comp";
import { fetchMediaList, isVideo } from "../../core/media.core";
import { useTranslation } from "../../core/translation.core";
import { Media } from "../../types/media";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import MagnifyingIcon from "../../icons/magnifying.icon";
import { inputCss } from "../../components/reuseables/native.comp";

export default function MediaPage() {
  const { t, msg } = useTranslation();

  return (
    <Layout title={t(msg.media.media)}>
      <section data-type="section">
        <UploadMediaForm />
      </section>
      <main>
        <MediaList />
      </main>
    </Layout>
  );
}

const MediaList = () => {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const { query } = router;
  const [size, setSize] = useState(40);
  const [searchInput, setSearchInput] = useState(typeof query.search === "string" ? query.search : "");
  const search = typeof query.search === "string" ? query.search : "";

  useEffect(() => {
    setSearchInput(typeof query.search === "string" ? query.search : "");
  }, [query.search]);

  const { data, isFetching } = useQuery({
    queryKey: ["mediaList", size, query.page || 1, search],
    queryFn: (context) =>
      fetchMediaList({
        pageSize: context.queryKey[1] as number,
        page: context.queryKey[2] as number,
        search: context.queryKey[3] as string,
      }),
  });

  function runSearch() {
    router.push({
      pathname: "/media",
      query: { ...query, page: 1, search: searchInput },
    });
  }

  if (isFetching) return <LoadingOvalCenterIcon />;
  if (!data) return null;

  return (
    <>
      <section data-type="section">
        <div className="grid grid-cols-[auto_200px] gap-2">
          <div className="relative grow">
            <div className="absolute top-1/2 -translate-y-1/2 left-2">
              <MagnifyingIcon />
            </div>
            <input
              id="media-search-input"
              className={`${inputCss} indent-8 w-full my-0 px-4 h-10 outline-none`}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  runSearch();
                }
              }}
              placeholder={t(msg.general.search)}
            />
          </div>
          <input id="media-search-btn" type="button" value={t(msg.general.search)} onClick={runSearch} />
        </div>
      </section>
      <div id="media-list" className="grid gap-4 grid-cols-[repeat(auto-fit,_minmax(8rem,_1fr))] justify-items-between">
        {data.list.map((media) => (
          <MediaItem key={media.id} media={media} />
        ))}
      </div>

      <PageNav page={Number(query.page) || 1} size={size} total={data.total} pathname="/media" query={query} />
    </>
  );
};

const MediaItem = ({ media }: { media: Media }) => {
  return (
    <Link className="group/item cursor-pointer overflow-hidden flex justify-center" href={`/media/${media.id}`}>
      {isVideo(media) && <video src={media.url} width="160" height="160" />}
      {!isVideo(media) && (
        <Image
          unoptimized
          className="border-2 border-white dark:border-gray-300/20 object-cover group-hover/item:border-red-50 transition-all max-w-36"
          src={media.thumbnailUrl}
          width="160"
          height="160"
          alt={media.filename}
        />
      )}
    </Link>
  );
};
